import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Decimal from 'decimal.js'
import { Maximize2, RefreshCw, X } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Banner, Button, ErrorState, Skeleton } from '@shared/design-system/ui'
import { formatMoney } from '@shared/lib/format/currency'
import { dashboardLiveQuery } from '../api/dashboard.queries'
import type { DashboardLiveFilters, DashboardLiveResult } from '../api/dashboard.queries'
import type { GrossSalesPoint } from '../api/dashboard.contract'
import type { ChartMode } from './gross-sales-series'
import { GrossSalesChart } from './GrossSalesChart'
import { srOnly } from './dashboard.css'
import * as styles from './live-gross-sales.css'

export const LIVE_POLL_MS = 30_000
const IDLE_MS = 3_000
const NEW_SALE_MS = 8_000
const CURRENCY = 'BRL'

export interface LiveGrossSalesPlaceholder {
  series: GrossSalesPoint[]
  periodLabel: string
}

interface LiveGrossSalesProps {
  filters: DashboardLiveFilters
  placeholder?: LiveGrossSalesPlaceholder
  mode: ChartMode
  onModeChange: (mode: ChartMode) => void
  onClose: () => void
}

function seriesTotal(series: readonly GrossSalesPoint[]) {
  return series.reduce((sum, point) => sum.plus(point.amount.amount), new Decimal(0))
}

function clock(timestamp: number) {
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(timestamp)
}

function placeholderResult(
  filters: DashboardLiveFilters,
  placeholder: LiveGrossSalesPlaceholder,
): DashboardLiveResult {
  return {
    generatedAt: '',
    window: { range: filters.range },
    periodOpen: true,
    byCurrency: { [CURRENCY]: { grossSalesSeries: placeholder.series } },
    periodLabel: placeholder.periodLabel,
  }
}

function useScreenWakeLock() {
  useEffect(() => {
    if (!('wakeLock' in navigator)) return
    let sentinel: WakeLockSentinel | null = null
    let disposed = false
    const acquire = async () => {
      if (document.visibilityState !== 'visible') return
      try {
        const next = await navigator.wakeLock.request('screen')
        if (disposed) {
          void next.release()
          return
        }
        sentinel = next
      } catch {
        sentinel = null
      }
    }
    const onVisibilityChange = () => {
      void acquire()
    }
    void acquire()
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
      void sentinel?.release().catch(() => undefined)
    }
  }, [])
}

function useIdle() {
  const [idle, setIdle] = useState(false)
  useEffect(() => {
    let timer = window.setTimeout(() => setIdle(true), IDLE_MS)
    const wake = () => {
      setIdle(false)
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setIdle(true), IDLE_MS)
    }
    const events = ['pointermove', 'pointerdown', 'keydown'] as const
    for (const event of events) window.addEventListener(event, wake)
    return () => {
      window.clearTimeout(timer)
      for (const event of events) window.removeEventListener(event, wake)
    }
  }, [])
  return idle
}

function useSaleIncrease(data: DashboardLiveResult | undefined, isPlaceholder: boolean) {
  const baseline = useRef<Decimal | null>(null)
  const [increase, setIncrease] = useState<{ amount: string; at: number } | null>(null)
  const [announcement, setAnnouncement] = useState('')

  useEffect(() => {
    if (!data || isPlaceholder) return
    const current = seriesTotal(data.byCurrency[CURRENCY]?.grossSalesSeries ?? [])
    const before = baseline.current
    baseline.current = current
    if (before && current.greaterThan(before)) {
      const amount = current.minus(before).toFixed(3)
      setIncrease({ amount, at: Date.now() })
      setAnnouncement(
        `Nova venda de ${formatMoney(amount, CURRENCY)}. Total no período: ${formatMoney(current.toFixed(3), CURRENCY)}.`,
      )
    }
  }, [data, isPlaceholder])

  useEffect(() => {
    if (!increase) return
    const timer = window.setTimeout(() => setIncrease(null), NEW_SALE_MS)
    return () => window.clearTimeout(timer)
  }, [increase])

  return { increase, announcement }
}

export function LiveGrossSales({
  filters,
  placeholder,
  mode,
  onModeChange,
  onClose,
}: LiveGrossSalesProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  const wasFullscreen = useRef(false)
  const [isFullscreen, setIsFullscreen] = useState(() => Boolean(document.fullscreenElement))
  const canFullscreen =
    typeof document.documentElement.requestFullscreen === 'function' &&
    document.fullscreenEnabled !== false
  const idle = useIdle()
  useScreenWakeLock()

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  const live = useQuery({
    ...dashboardLiveQuery(filters),
    placeholderData: placeholder ? placeholderResult(filters, placeholder) : undefined,
    refetchInterval: (query) =>
      query.state.status === 'error' || query.state.data?.periodOpen === false
        ? false
        : LIVE_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  })
  const { increase, announcement } = useSaleIncrease(live.data, live.isPlaceholderData)

  useEffect(() => {
    const root = rootRef.current
    root?.focus()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onFullscreenChange = () => {
      const active = Boolean(document.fullscreenElement)
      setIsFullscreen(active)
      if (wasFullscreen.current && !active) onCloseRef.current()
      wasFullscreen.current = active
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab' || !root) return
      const focusable = Array.from(
        root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      )
      const first = focusable[0]
      const last = focusable.at(-1)
      if (!first || !last) return
      if (event.shiftKey && (document.activeElement === first || document.activeElement === root)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('fullscreenchange', onFullscreenChange)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('fullscreenchange', onFullscreenChange)
      document.removeEventListener('keydown', onKeyDown)
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined)
    }
  }, [])

  const data = live.data
  const series = data?.byCurrency[CURRENCY]?.grossSalesSeries ?? []
  const failed = live.isError
  const closed = data?.periodOpen === false
  const syncing = live.isPlaceholderData || (live.isPending && !data)
  const status = failed
    ? { tone: 'paused' as const, label: 'Atualização pausada' }
    : closed
      ? { tone: 'idle' as const, label: 'Período encerrado' }
      : syncing
        ? { tone: 'idle' as const, label: 'Atualizando' }
        : { tone: 'live' as const, label: 'Ao vivo' }

  return createPortal(
    <div
      ref={rootRef}
      className={styles.root}
      role="dialog"
      aria-modal="true"
      aria-label="Bruto vendido ao vivo"
      tabIndex={-1}
      data-idle={idle ? 'true' : 'false'}
      data-testid="dashboard-live-screen"
    >
      <div className={styles.topbar}>
        <span className={styles.status}>
          <span className={styles.dot({ tone: status.tone })} aria-hidden="true" />
          {status.label}
          {live.dataUpdatedAt > 0 && !live.isPlaceholderData ? (
            <span className={styles.updatedAt}>· Atualizado às {clock(live.dataUpdatedAt)}</span>
          ) : null}
        </span>
        <div className={styles.controls}>
          {canFullscreen && !isFullscreen ? (
            <Button
              variant="secondary"
              data-testid="dashboard-live-fullscreen-btn"
              size="sm"
              leftIcon={<Maximize2 size={14} aria-hidden />}
              onClick={() => {
                void document.documentElement.requestFullscreen().catch(() => undefined)
              }}
            >
              Tela cheia
            </Button>
          ) : null}
          <Button
            variant="secondary"
            data-testid="dashboard-live-exit-btn"
            size="sm"
            leftIcon={<X size={14} aria-hidden />}
            onClick={() => onCloseRef.current()}
          >
            Sair
          </Button>
        </div>
      </div>

      {failed && data ? (
        <Banner
          tone="warning"
          title="Não foi possível atualizar"
          action={
            <Button
              variant="secondary"
              data-testid="dashboard-live-retry-btn"
              size="sm"
              leftIcon={<RefreshCw size={14} aria-hidden />}
              disabled={live.isFetching}
              onClick={() => {
                void live.refetch()
              }}
            >
              Tentar novamente
            </Button>
          }
        >
          Os valores são da última atualização bem-sucedida e podem estar desatualizados.
        </Banner>
      ) : null}
      {closed && !failed ? (
        <Banner tone="info" title="Período encerrado">
          Este período terminou e não recebe novas vendas. Escolha outro período no dashboard para
          acompanhar ao vivo.
        </Banner>
      ) : null}

      {data ? (
        <GrossSalesChart
          series={series}
          currency={CURRENCY}
          periodLabel={data.periodLabel}
          mode={mode}
          onModeChange={onModeChange}
          variant="live"
          highlight={
            increase ? (
              <span key={increase.at} className={styles.increase}>
                +{formatMoney(increase.amount, CURRENCY)}
              </span>
            ) : null
          }
        />
      ) : failed ? (
        <div className={styles.fill}>
          <ErrorState
            onRetry={() => {
              void live.refetch()
            }}
          />
        </div>
      ) : (
        <div className={styles.fill}>
          <Skeleton width="40%" height={20} />
          <Skeleton width="60%" height={72} />
          <Skeleton width="100%" height="100%" />
        </div>
      )}

      <span className={srOnly} aria-live="polite">
        {announcement}
      </span>
    </div>,
    document.body,
  )
}
